import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Grid3X3, List, Search, SlidersHorizontal, MoreHorizontal, Merge, Wand2, X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWardrobeStore } from '../../stores/wardrobeStore';
import { GarmentCard } from './GarmentCard';
import { AddGarmentModal } from './AddGarmentModal';
import { ExistingGarmentThumb } from './ExistingGarmentThumb';
import { FilterBar } from '../common/FilterBar';
import { getGarmentImages, saveGarmentImages } from '@/utils/garmentStorage';
import type { Garment, GarmentColor } from '../../types';
import toast from 'react-hot-toast';
import { getEmbedding, cosineSimilarity, SIMILARITY_THRESHOLD } from '@/services/clothingSimilarity';


export function ClosetPage() {
  const { garments, filters, setFilters, fetchGarments, mergeGarments } = useWardrobeStore();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [mergeMode, setMergeMode] = useState<'off' | 'manual' | 'suggested'>('off');
  const [mergeSelection, setMergeSelection] = useState<Set<string>>(new Set());
  const [suggestedGroups, setSuggestedGroups] = useState<string[][]>([]);
  const [currentSuggestion, setCurrentSuggestion] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  useEffect(() => {
    fetchGarments(true);
  }, []);


  // Suggest merges using ML-based visual similarity model (MobileNetV3, 95.7% accuracy).
  // No fallbacks - if the model fails, we show an error rather than bad suggestions.
  const suggestMerges = async (): Promise<string[][]> => {
    const groups: string[][] = [];
    const used = new Set<string>();

    // Pre-filter: only compare garments with same category
    const byCategory: Record<string, Garment[]> = {};
    for (const g of garments) {
      const key = `${g.category}:${g.subCategory}`;
      (byCategory[key] ??= []).push(g);
    }

    for (const sameType of Object.values(byCategory)) {
      if (sameType.length < 2) continue;

      // Get ML embeddings for all garments in this category
      const embeddings: Record<string, Float32Array> = {};
      const embPromises = sameType.map(async (g) => {
        let imgUrl = g.imageUrls?.[0] || g.thumbnailUrl;
        if (!imgUrl) return null;
        // Resolve idb:// URLs from IndexedDB
        if (imgUrl.startsWith('idb://')) {
          const match = imgUrl.match(/^idb:\/\/(.+)\/(\d+)$/);
          if (match) {
            const stored = await getGarmentImages(match[1]);
            imgUrl = stored?.[parseInt(match[2], 10)] || '';
          }
          if (!imgUrl || imgUrl.startsWith('idb://')) return null;
        }
        const emb = await getEmbedding(imgUrl);
        return { id: g.id, emb };
      });

      const results = await Promise.all(embPromises);
      for (const r of results) {
        if (r) embeddings[r.id] = r.emb;
      }

      for (let i = 0; i < sameType.length; i++) {
        if (used.has(sameType[i].id)) continue;
        if (!embeddings[sameType[i].id]) continue;
        const gi = sameType[i];
        const group = [gi.id];

        for (let j = i + 1; j < sameType.length; j++) {
          if (used.has(sameType[j].id)) continue;
          if (!embeddings[sameType[j].id]) continue;

          const score = cosineSimilarity(embeddings[gi.id], embeddings[sameType[j].id]);
          if (score >= SIMILARITY_THRESHOLD) {
            group.push(sameType[j].id);
          }
        }

        if (group.length > 1) {
          for (const id of group) used.add(id);
          groups.push(group);
        }
      }
    }

    return groups;
  };

  const startSuggestMerges = async () => {
    setShowMenu(false);
    toast.loading('Analyzing garment similarity...', { id: 'merge-analyze' });
    try {
      const groups = await suggestMerges();
      toast.dismiss('merge-analyze');
      if (groups.length === 0) {
        toast('No merge suggestions found', { icon: '👌' });
        setMergeMode('off');
        return;
      }
      setSuggestedGroups(groups);
      setCurrentSuggestion(0);
      setMergeMode('suggested');
    } catch {
      toast.dismiss('merge-analyze');
      toast.error('Failed to analyze similarities');
      setMergeMode('off');
    }
  };

  const startManualMerge = () => {
    setMergeMode('manual');
    setMergeSelection(new Set());
    setShowMenu(false);
  };

  const executeMerge = async (ids: string[]) => {
    if (ids.length < 2) return;
    const primary = garments.find(g => g.id === ids[0]);
    if (!primary) return;

    // Resolve all idb:// URLs to actual data URLs and collect them
    const allRealImages: string[] = [];
    for (const id of ids) {
      const g = garments.find(g => g.id === id);
      if (!g) continue;
      // Try to get real images from IndexedDB
      const idbImages = await getGarmentImages(id).catch(() => null);
      if (idbImages && idbImages.length > 0) {
        allRealImages.push(...idbImages);
      } else if (g.imageUrls?.length) {
        // If not idb refs, use directly
        allRealImages.push(...g.imageUrls.filter(u => !u.startsWith('idb://')));
      }
    }

    if (allRealImages.length === 0) {
      toast.error('No images to merge');
      return;
    }

    // Save all merged images to IndexedDB under primary's ID
    await saveGarmentImages(primary.id, allRealImages);
    const mergedImageUrls = allRealImages.map((_, i) => `idb://${primary.id}/${i}`);

    // Update primary with merged images + remove others silently
    await mergeGarments(primary.id, mergedImageUrls, ids.slice(1));
  };

  const handleSuggestionAccept = async () => {
    const group = suggestedGroups[currentSuggestion];
    if (group) await executeMerge(group);
    if (currentSuggestion + 1 < suggestedGroups.length) {
      setCurrentSuggestion(prev => prev + 1);
    } else {
      setMergeMode('off');
      setSuggestedGroups([]);
    }
  };

  const handleSuggestionSkip = () => {
    if (currentSuggestion + 1 < suggestedGroups.length) {
      setCurrentSuggestion(prev => prev + 1);
    } else {
      setMergeMode('off');
      setSuggestedGroups([]);
    }
  };

  const handleManualMerge = async () => {
    const ids = Array.from(mergeSelection);
    if (ids.length < 2) return;
    await executeMerge(ids);
    setMergeSelection(new Set());
  };

  const toggleMergeSelect = (id: string) => {
    setMergeSelection(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredGarments = useMemo(() => {
    let result = [...garments];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(g =>
        g.name?.toLowerCase().includes(q) ||
        g.category?.toLowerCase().includes(q) ||
        g.brand?.toLowerCase().includes(q) ||
        g.subCategory?.toLowerCase().includes(q)
      );
    }
    if (filters.category) result = result.filter(g => g.category === filters.category);
    if (filters.color) result = result.filter(g => g.colors?.some((c: GarmentColor) => c === filters.color));
    if (filters.season) result = result.filter(g => g.seasons?.includes(filters.season!));
    if (filters.sortBy === 'recent') result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    if (filters.sortBy === 'most-worn') result.sort((a, b) => b.timesWorn - a.timesWorn);
    if (filters.sortBy === 'least-worn') result.sort((a, b) => a.timesWorn - b.timesWorn);
    return result;
  }, [garments, filters, searchQuery]);

  return (
    <div className="flex flex-col h-full bg-[#FAFAF8]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#FAFAF8]/95 backdrop-blur-sm px-4 pt-4 pb-2">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>
            My Closet
          </h1>
          <div className="flex gap-2">
            <button onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')} className="p-2 rounded-lg bg-white border border-gray-200">
              {viewMode === 'grid' ? <List size={18} /> : <Grid3X3 size={18} />}
            </button>
            {/* ··· menu with merge options */}
            <div className="relative" ref={menuRef}>
              <button onClick={() => setShowMenu(!showMenu)} className="p-2 rounded-lg bg-white border border-gray-200">
                <MoreHorizontal size={18} />
              </button>
              <AnimatePresence>
                {showMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -4 }}
                    className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-30"
                  >
                    <button
                      onClick={startSuggestMerges}
                      className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 hover:bg-gray-50 transition-colors"
                    >
                      <Wand2 size={16} className="text-[#6C3CE1]" />
                      Suggest Merges
                    </button>
                    <button
                      onClick={startManualMerge}
                      className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 hover:bg-gray-50 transition-colors"
                    >
                      <Merge size={16} className="text-[#6C3CE1]" />
                      Manually Merge
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
        {/* Search bar with filter icon inside */}
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search your wardrobe..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-11 py-2.5 rounded-xl bg-white border border-gray-200 focus:border-[#6C3CE1] focus:ring-1 focus:ring-[#6C3CE1] outline-none text-sm"
          />
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-colors ${
              showFilters ? 'bg-[#6C3CE1] text-white' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }`}
          >
            <SlidersHorizontal size={16} />
          </button>
        </div>
        <AnimatePresence>{showFilters && <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}><FilterBar filters={filters} onFilterChange={setFilters} onReset={() => setFilters({ sortBy: 'recent', sortOrder: 'desc' })} /></motion.div>}</AnimatePresence>
      </div>

      {/* Smart Collections */}
      <div className="px-4 py-2 flex gap-2 overflow-x-auto no-scrollbar">
        {([
          { label: 'All', sort: 'recent' as const },
          { label: 'Unworn 30+ days', sort: 'least-worn' as const },
          { label: 'Most Versatile', sort: 'most-worn' as const },
          { label: 'Recent', sort: 'recent' as const },
        ]).map(tag => {
          const isActive = filters.sortBy === tag.sort;
          return (
            <button key={tag.label} onClick={() => setFilters({ sortBy: tag.sort })}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors ${
                isActive ? 'bg-[#6C3CE1] text-white border-[#6C3CE1]' : 'bg-white border-gray-200 hover:border-[#6C3CE1] hover:text-[#6C3CE1]'
              }`}>
              {tag.label}
            </button>
          );
        })}
      </div>

      {/* Merge mode banners */}
      <AnimatePresence>
        {mergeMode === 'suggested' && suggestedGroups.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-4 py-3 bg-[#6C3CE1]/5 border-b border-[#6C3CE1]/20"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-[#6C3CE1]">
                Merge suggestion {currentSuggestion + 1} of {suggestedGroups.length}
              </p>
              <button onClick={() => { setMergeMode('off'); setSuggestedGroups([]); }} className="text-gray-400 hover:text-gray-600">
                <X size={16} />
              </button>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {suggestedGroups[currentSuggestion]?.map(id => {
                const g = garments.find(g => g.id === id);
                if (!g) return null;
                return (
                  <ExistingGarmentThumb key={id} garment={g} size="sm" />
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Same {garments.find(g => g.id === suggestedGroups[currentSuggestion]?.[0])?.category} - merge into one?
            </p>
            <div className="flex gap-2 mt-2">
              <button onClick={handleSuggestionAccept} className="flex-1 py-2 rounded-lg bg-[#6C3CE1] text-white text-sm font-medium flex items-center justify-center gap-1">
                <Check size={14} /> Merge
              </button>
              <button onClick={handleSuggestionSkip} className="flex-1 py-2 rounded-lg bg-white border border-gray-200 text-sm text-gray-600">
                Skip
              </button>
            </div>
          </motion.div>
        )}

        {mergeMode === 'manual' && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-4 py-3 bg-[#6C3CE1]/5 border-b border-[#6C3CE1]/20"
          >
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-[#6C3CE1]">
                Select garments to merge ({mergeSelection.size} selected)
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleManualMerge}
                  disabled={mergeSelection.size < 2}
                  className="px-3 py-1.5 rounded-lg bg-[#6C3CE1] text-white text-xs font-medium disabled:opacity-40"
                >
                  Merge ({mergeSelection.size})
                </button>
                <button onClick={() => { setMergeMode('off'); setMergeSelection(new Set()); }} className="text-gray-400 hover:text-gray-600">
                  <X size={16} />
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Tap items to select, then merge</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Garment Grid */}
      <div className="flex-1 overflow-y-auto px-4 pb-24">
        {filteredGarments.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <Plus size={24} />
            </div>
            <p className="text-sm font-medium">Your closet is empty</p>
            <p className="text-xs mt-1">Tap + to add your first item</p>
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-2' : 'flex flex-col gap-2 mt-2'}>
            {filteredGarments.map(garment => (
              <motion.div
                key={garment.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={() => {
                  if (mergeMode === 'manual') {
                    toggleMergeSelect(garment.id);
                  } else {
                    navigate(`/closet/${garment.id}`);
                  }
                }}
                className={`relative ${mergeMode === 'manual' ? 'cursor-pointer' : ''}`}
              >
                <GarmentCard garment={garment} viewMode={viewMode} />
                {/* Merge selection overlay */}
                {mergeMode === 'manual' && (
                  <div className={`absolute inset-0 rounded-2xl transition-colors ${
                    mergeSelection.has(garment.id) ? 'bg-[#6C3CE1]/20 ring-2 ring-[#6C3CE1]' : 'hover:bg-[#6C3CE1]/5'
                  }`}>
                    {mergeSelection.has(garment.id) && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#6C3CE1] text-white flex items-center justify-center">
                        <Check size={14} strokeWidth={3} />
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
        <div className="text-center text-xs text-gray-400 mt-4 mb-2">
          {filteredGarments.length} items
        </div>
      </div>

      {/* FAB */}
      <motion.button
        whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
        onClick={() => setShowAddModal(true)}
        className="fixed bottom-24 right-6 w-14 h-14 rounded-full bg-[#6C3CE1] text-white shadow-lg flex items-center justify-center z-20"
      >
        <Plus size={24} />
      </motion.button>

      {showAddModal && <AddGarmentModal onClose={() => setShowAddModal(false)} />}
    </div>
  );
}
